import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProgramsInvitation as Invitation,
  ProgramsParticipant as Participant,
  ProgramsProgram as Program,
  ProgramsProgramCreate as ProgramCreate,
  ProgramsProgramReport as ProgramReport,
  ProgramsProgramReportCreate as ProgramReportCreate,
  ProgramsProgramStatus as ProgramStatus,
  ProgramsProgramUpdate as ProgramUpdate,
  ProgramsReportConclusion as ReportConclusion,
} from '../generated/api/types.gen.ts';
import { programsClose, programsCreate, programsGet, programsGetReport, programsInvite, programsList, programsListInvitations, programsListParticipants, programsUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { Invitation, Participant, Program, ProgramCreate, ProgramReport, ProgramReportCreate, ProgramStatus, ProgramUpdate, ReportConclusion };

export interface ProgramFilter {
  status?: ProgramStatus;
  productId?: string;
}

export const SIDESTØRRELSE = 50;

/** Nøklene eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). */
export const programNoekkel = {
  alle: ['programs'] as const,
  liste: (filter: ProgramFilter) => ['programs', 'liste', filter] as const,
  detalj: (id: string) => ['programs', 'detalj', id] as const,
  invitasjoner: (id: string) => ['programs', 'invitasjoner', id] as const,
  deltakere: (id: string) => ['programs', 'deltakere', id] as const,
  rapport: (id: string) => ['programs', 'rapport', id] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Programmene, nyeste øverst — sortering og markør er serverens. */
export function useProgrammer(filter: ProgramFilter = {}) {
  const klient = useApiKlient();
  return useInfiniteQuery({
    queryKey: programNoekkel.liste(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await programsList({ client: klient, query: { ...filter, cursor: pageParam, limit: SIDESTØRRELSE } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as { items: Program[]; nextCursor?: string };
    },
    getNextPageParam: (siste) => siste.nextCursor ?? undefined,
  });
}

/** Programsiden (4c) med tellerne. Tellerne er utledet av serveren og lagres aldri. */
export function useProgram(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: programNoekkel.detalj(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<Program> => {
      const res = await programsGet({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Program;
    },
  });
}

export function useOpprettProgram() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ProgramCreate): Promise<Program> => {
      const res = await programsCreate({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Program;
    },
    onSuccess: (program) => {
      queryClient.setQueryData(programNoekkel.detalj(program.id), program);
      void queryClient.invalidateQueries({ queryKey: programNoekkel.alle });
    },
  });
}

/** Delvis oppdatering; statusovergangene går her — aapen og avbrutt, aldri aktiv (M3.3). */
export function useOppdaterProgram() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ProgramUpdate }): Promise<Program> => {
      const res = await programsUpdate({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Program;
    },
    onSuccess: (program) => {
      queryClient.setQueryData(programNoekkel.detalj(program.id), program);
      void queryClient.invalidateQueries({ queryKey: programNoekkel.alle });
    },
  });
}

/** Utsendte invitasjoner uten token — sendt, akseptert, avslått, utløpt. */
export function useInvitasjoner(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: programNoekkel.invitasjoner(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<Invitation[]> => {
      const res = await programsListInvitations({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Invitation[];
    },
  });
}

/** Deltakerne med flaggstatus (DD-36). Ingen sortering på aktivitet, ingen score (DD-38). */
export function useDeltakere(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: programNoekkel.deltakere(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<Participant[]> => {
      const res = await programsListParticipants({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Participant[];
    },
  });
}

/**
 * Den frosne rapporten (M3.4). 404 før programmet er avsluttet — det er en tilstand, ikke en
 * feil, så `retry: false`: å prøve på nytt gjør ingen rapport ferdig.
 */
export function useRapport(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: programNoekkel.rapport(id ?? ''),
    enabled: id !== null,
    retry: false,
    queryFn: async (): Promise<ProgramReport> => {
      const res = await programsGetReport({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as ProgramReport;
    },
  });
}

/**
 * Avslutter programmet og fryser rapporten. Tallene regnes av serveren i samme øyeblikk —
 * panelet sender konklusjonen, ikke tellingene (DD-16: ett sett tall, ett sted).
 */
export function useAvsluttProgram(id: string) {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ProgramReportCreate): Promise<ProgramReport> => {
      const res = await programsClose({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as ProgramReport;
    },
    onSuccess: (rapport) => {
      queryClient.setQueryData(programNoekkel.rapport(id), rapport);
      void queryClient.invalidateQueries({ queryKey: programNoekkel.alle });
    },
  });
}

/**
 * Inviterer pseudonymer. Tokenene finnes KUN i svaret her og i webhooken
 * `program.invitation_created` — panelet viser dem aldri i en liste, og lagrer dem ikke.
 * Leietakeren løser pseudonym → bruker og sender selv (DD-12).
 */
export function useInviter(id: string) {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subjects: string[]): Promise<{ invitations: Invitation[] }> => {
      const res = await programsInvite({ client: klient, path: { id }, body: { subjects } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as { invitations: Invitation[] };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: programNoekkel.invitasjoner(id) });
      void queryClient.invalidateQueries({ queryKey: programNoekkel.detalj(id) });
    },
  });
}
